/**
 * Modelo do relatório premium IA/BOT — mock e mapeamento a partir do estado da API.
 * Ajuste VALOR_HORA_PADRAO_REAIS ou substitua por custo vindo da API quando disponível.
 */
export const VALOR_HORA_PADRAO_REAIS = 16

const brlHoraFmt = `R$ ${VALOR_HORA_PADRAO_REAIS.toFixed(2).replace('.', ',')}`

/** Texto curto para tooltip ao lado de “Custo economizado”. */
export const TEXTO_CUSTO_TOOLTIP_CURTO = `Estimativa baseada nas horas de atendimento humano evitadas com o bot e em um custo médio de ${brlHoraFmt} por hora de atendimento humano.`

/** Texto longo (tooltip expandido) sobre o valor em R$. */
export const TEXTO_CUSTO_TOOLTIP_LONGO = `O valor corresponde às horas de trabalho humano estimadas que deixaram de ser necessárias (diferença entre o tempo sem automação e o tempo com bot no período), multiplicadas por um custo médio de ${brlHoraFmt} por hora.`

function formatarDataLabelPt(iso) {
  const d = new Date(`${iso}T12:00:00`)
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
}

function formatarMoedaBrl(n) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(n)
}

/** Formato explícito horas + minutos a partir de segundos (ex.: 145h 11min). Base do relatório unificado. */
export function formatarHorasMinutosDeSegundos(seg) {
  if (seg == null || seg < 0) return '0 min'
  if (seg === 0) return '0 min'
  const totalMin = Math.round(seg / 60)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  if (h === 0) return `${m} min`
  const hFmt = h.toLocaleString('pt-BR')
  if (m === 0) return `${hFmt} h`
  return `${hFmt}h ${m}min`
}

function formatarDataAtePeriodo(d) {
  if (d == null || d === '') return ''
  const x = d instanceof Date ? d : new Date(d)
  if (Number.isNaN(x.getTime())) return ''
  return x.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' })
}

/** Texto para anotação no gráfico: “N horas poupadas até d de mês.” */
function formatarLabelHorasPoupadasData(seg, dataAte) {
  if (seg == null || seg <= 0) {
    const sufixo = dataAte ? ` até ${dataAte}` : ''
    return `0 horas poupadas${sufixo}`
  }
  const totalMin = Math.round(seg / 60)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  const hFmt = h.toLocaleString('pt-BR')
  const corp = m === 0 ? `${hFmt} horas poupadas` : `${hFmt}h ${String(m).padStart(2, '0')}min poupadas`
  const sufixo = dataAte ? ` até ${dataAte}` : ''
  return `${corp}${sufixo}`
}

function getFimReferenciaString(r) {
  return r.fim || (r.ultima_mensagem && r.ultima_mensagem.timestamp) || r.ultima_mensagem_em || r.data_resolucao || r.inicio
}

function getFimMsReport(r) {
  const f = getFimReferenciaString(r)
  if (f == null || f === '') return null
  const t = new Date(f).getTime()
  return Number.isNaN(t) ? null : t
}

function dateToKeyYmd(d) {
  const x = new Date(d)
  const y = x.getFullYear()
  const m = String(x.getMonth() + 1).padStart(2, '0')
  const day = String(x.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * Tempo de atendimento humano acumulado por registro (segundos).
 * - Com fim_bot: de primeira_mensagem_atendente_em até fim.
 * - Sem fim_bot, atendimento_bot === false e atendente_ativo: de inicio até fim.
 */
export function calcularTempoAtendenteSegundos(r) {
  const fimMs = getFimMsReport(r)
  if (fimMs == null) return 0

  const temFimBot = r.fim_bot != null && r.fim_bot !== ''

  if (temFimBot) {
    if (r.primeira_mensagem_atendente_em == null || r.primeira_mensagem_atendente_em === '') return 0
    const desdeMs = new Date(r.primeira_mensagem_atendente_em).getTime()
    if (Number.isNaN(desdeMs)) return 0
    if (fimMs < desdeMs) return 0
    return (fimMs - desdeMs) / 1000
  }

  if (r.atendimento_bot !== false) return 0
  if (r.atendente_ativo == null) return 0

  if (r.inicio == null || r.inicio === '') return 0
  const iniMs = new Date(r.inicio).getTime()
  if (Number.isNaN(iniMs)) return 0
  if (fimMs < iniMs) return 0
  return (fimMs - iniMs) / 1000
}

/**
 * Chave estável para merge de intervalos (humano ou bot): mesmo atendente ou mesma conversa.
 */
function getChaveMergePorRegistro(r) {
  const aa = r.atendente_ativo
  if (aa && typeof aa === 'object' && (aa.id != null || aa.id === 0)) {
    return String(aa.id)
  }
  if (r.id != null && r.id !== '') return `__conv__${r.id}`
  const ini = r.inicio || ''
  const fi = getFimReferenciaString(r) || ''
  return `__row__${ini}__${fi}`
}

/**
 * Intervalo [início, fim] em ms para tempo humano ocupado, alinhado a calcularTempoAtendenteSegundos.
 */
function intervaloHumanoMergeMs(r) {
  const fimMs = getFimMsReport(r)
  if (fimMs == null) return null

  const temFimBot = r.fim_bot != null && r.fim_bot !== ''

  if (temFimBot) {
    if (r.primeira_mensagem_atendente_em == null || r.primeira_mensagem_atendente_em === '') return null
    const desdeMs = new Date(r.primeira_mensagem_atendente_em).getTime()
    if (Number.isNaN(desdeMs) || fimMs < desdeMs) return null
    return { startMs: desdeMs, endMs: fimMs }
  }

  if (r.atendimento_bot !== false) return null
  if (r.atendente_ativo == null) return null
  if (r.inicio == null || r.inicio === '') return null
  const iniMs = new Date(r.inicio).getTime()
  if (Number.isNaN(iniMs) || fimMs < iniMs) return null
  return { startMs: iniMs, endMs: fimMs }
}

function mergeIntervalosOrdenados(intervals) {
  if (!intervals.length) return []
  const sorted = [...intervals].sort((a, b) => a.startMs - b.startMs)
  const out = []
  let cur = { ...sorted[0] }
  for (let i = 1; i < sorted.length; i += 1) {
    const n = sorted[i]
    if (n.startMs <= cur.endMs) {
      cur.endMs = Math.max(cur.endMs, n.endMs)
    } else {
      out.push(cur)
      cur = { ...n }
    }
  }
  out.push(cur)
  return out
}

/** Por chave de atendente: intervalos [startMs,endMs] já fundidos (sem sobreposição). */
function buildIntervalosHumanosMergeadosPorChave(lista) {
  const byKey = new Map()
  lista.forEach(r => {
    const iv = intervaloHumanoMergeMs(r)
    if (iv == null) return
    const key = getChaveMergePorRegistro(r)
    if (!byKey.has(key)) byKey.set(key, [])
    byKey.get(key).push(iv)
  })
  const merged = new Map()
  byKey.forEach((intervals, key) => {
    merged.set(key, mergeIntervalosOrdenados(intervals))
  })
  return merged
}

function nextDayStartLocalMs(fromMs) {
  const d = new Date(fromMs)
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime()
}

function acumularSegmentoNoMapaDiarioSegundos(dailySec, startMs, endMs) {
  let cur = startMs
  while (cur < endMs) {
    const dayEnd = nextDayStartLocalMs(cur)
    const chunkEnd = Math.min(endMs, dayEnd)
    const key = dateToKeyYmd(cur)
    const sec = (chunkEnd - cur) / 1000
    const next = (dailySec[key] || 0) + sec
    Object.assign(dailySec, { [key]: next })
    cur = chunkEnd
  }
}

/**
 * Tempo humano ocupado no período: merge de sobreposições por atendente, depois soma global.
 * Retorna ms, minutos, segundos totais e mapa diário (segundos/dia) para o gráfico «sem bot».
 */
export function calcularTempoHumanoOcupadoMerge(lista) {
  const merged = buildIntervalosHumanosMergeadosPorChave(lista)
  let totalMs = 0
  const tempoAtendentesPorDia = {}
  merged.forEach(segs => {
    segs.forEach(s => {
      const dur = s.endMs - s.startMs
      if (dur <= 0) return
      totalMs += dur
      acumularSegmentoNoMapaDiarioSegundos(tempoAtendentesPorDia, s.startMs, s.endMs)
    })
  })
  const totalSeg = totalMs / 1000
  const totalMin = totalMs / 60000
  return {
    tempoTotalHumanoMs: Math.round(totalMs),
    tempoTotalHumanoMinutos: Math.round(totalMin * 10) / 10,
    tempoTotalAtendentesSeg: totalSeg,
    tempoAtendentesPorDia,
  }
}

function temAtendenteAtivo(r) {
  const aa = r.atendente_ativo
  if (aa == null) return false
  if (aa === true) return true
  if (typeof aa === 'object' && (aa.id != null || aa.id === 0)) return true
  return false
}

/**
 * Tempo de atendimento com bot por registro (segundos).
 * 1) Com atendente ativo e fim_bot: intervalo inicio → fim_bot (inclui atendente_ativo === true ou objeto com id, conforme API).
 * 2) Com status resolvido e atendimento_bot === true: intervalo inicio → ultima_mensagem.timestamp (apenas; sem ultima_mensagem_em nem fim).
 * A regra 1 tem prioridade quando ambas se aplicam.
 */
export function calcularTempoBotSegundos(r) {
  if (r.inicio == null || r.inicio === '') return 0
  const iniMs = new Date(r.inicio).getTime()
  if (Number.isNaN(iniMs)) return 0

  const temFimBot = r.fim_bot != null && r.fim_bot !== ''

  if (temAtendenteAtivo(r) && temFimBot) {
    const fimBotMs = new Date(r.fim_bot).getTime()
    if (Number.isNaN(fimBotMs) || fimBotMs < iniMs) return 0
    return (fimBotMs - iniMs) / 1000
  }

  const statusOk = r.status === 'resolvido' || r.status === 'RESOLVIDO'
  if (statusOk && r.atendimento_bot === true) {
    const um = r.ultima_mensagem
    const ts = um && um.timestamp != null && um.timestamp !== '' ? um.timestamp : null
    if (ts == null) return 0
    const ultMs = new Date(ts).getTime()
    if (Number.isNaN(ultMs) || ultMs < iniMs) return 0
    return (ultMs - iniMs) / 1000
  }

  return 0
}

/**
 * Intervalo [início, fim] em ms para fase com bot, alinhado a calcularTempoBotSegundos.
 */
function intervaloBotMergeMs(r) {
  if (r.inicio == null || r.inicio === '') return null
  const iniMs = new Date(r.inicio).getTime()
  if (Number.isNaN(iniMs)) return null

  const temFimBot = r.fim_bot != null && r.fim_bot !== ''

  if (temAtendenteAtivo(r) && temFimBot) {
    const fimBotMs = new Date(r.fim_bot).getTime()
    if (Number.isNaN(fimBotMs) || fimBotMs < iniMs) return null
    return { startMs: iniMs, endMs: fimBotMs }
  }

  const statusOk = r.status === 'resolvido' || r.status === 'RESOLVIDO'
  if (statusOk && r.atendimento_bot === true) {
    const um = r.ultima_mensagem
    const ts = um && um.timestamp != null && um.timestamp !== '' ? um.timestamp : null
    if (ts == null) return null
    const ultMs = new Date(ts).getTime()
    if (Number.isNaN(ultMs) || ultMs < iniMs) return null
    return { startMs: iniMs, endMs: ultMs }
  }

  return null
}

function buildIntervalosBotMergeadosPorChave(lista) {
  const byKey = new Map()
  lista.forEach(r => {
    const iv = intervaloBotMergeMs(r)
    if (iv == null) return
    const key = getChaveMergePorRegistro(r)
    if (!byKey.has(key)) byKey.set(key, [])
    byKey.get(key).push(iv)
  })
  const merged = new Map()
  byKey.forEach((intervals, key) => {
    merged.set(key, mergeIntervalosOrdenados(intervals))
  })
  return merged
}

/**
 * Tempo com bot no período: merge de sobreposições por atendente/conversa, mapa diário para o gráfico «com bot».
 */
export function calcularTempoBotOcupadoMerge(lista) {
  const merged = buildIntervalosBotMergeadosPorChave(lista)
  let totalMs = 0
  const tempoBotPorDia = {}
  merged.forEach(segs => {
    segs.forEach(s => {
      const dur = s.endMs - s.startMs
      if (dur <= 0) return
      totalMs += dur
      acumularSegmentoNoMapaDiarioSegundos(tempoBotPorDia, s.startMs, s.endMs)
    })
  })
  const totalSeg = totalMs / 1000
  const totalMin = totalMs / 60000
  return {
    tempoTotalBotMs: Math.round(totalMs),
    tempoTotalBotMinutos: Math.round(totalMin * 10) / 10,
    tempoTotalBotSeg: totalSeg,
    tempoBotPorDia,
  }
}

function buildComparativoBlocos({
  botMinMedio,
  humanoMinComparacao,
  velocidadeX,
  pctTaxaBot,
  pctTaxaHumano,
  pctParticipacaoBot,
  pctParticipacaoHumano,
}) {
  const bMin = Number(botMinMedio)
  const hMin = Number(humanoMinComparacao)
  const maxT = Math.max(bMin || 0, hMin || 1, 1)
  const pctBarBotTempo = bMin > 0 ? Math.min(100, Math.round((bMin / maxT) * 100)) : 0
  const pctBarHumTempo = hMin > 0 ? Math.min(100, Math.round((hMin / maxT) * 100)) : 100

  return [
    {
      key: 'tempo',
      titulo: 'Tempo médio de resolução',
      subtitulo: 'Menor tempo costuma significar atendimento mais ágil.',
      botLinha: `Bot: ${bMin > 0 ? `${bMin} min` : '—'}`,
      humanoLinha: `Humano: ${hMin > 0 ? `${hMin} min` : '—'}`,
      destaque: velocidadeX > 0 ? `Bot é ${String(velocidadeX).replace('.', ',')}× mais rápido` : '—',
      barBotPct: pctBarBotTempo,
      barHumanoPct: pctBarHumTempo,
      modoBarra: 'dupla-tempo',
    },
    {
      key: 'taxa',
      titulo: 'Taxa de resolução',
      subtitulo: 'Percentual de conversas encerradas com sucesso em cada canal.',
      botLinha: `Bot: ${pctTaxaBot}%`,
      humanoLinha: `Humano: ${pctTaxaHumano}%`,
      destaque: (() => {
        if (pctTaxaBot > pctTaxaHumano) return 'O bot resolveu mais atendimentos sem repasse no período.'
        if (pctTaxaBot === pctTaxaHumano) return 'Bot e humano com taxa semelhante neste período.'
        return 'Humano com taxa de resolução maior neste período.'
      })(),
      barBotPct: Math.min(100, pctTaxaBot),
      barHumanoPct: Math.min(100, pctTaxaHumano),
      modoBarra: 'dupla-pct',
    },
    {
      key: 'participacao',
      titulo: 'Participação no período',
      subtitulo: 'Quem absorveu mais conversas com bot no intervalo.',
      botLinha: `Bot: ${pctParticipacaoBot}%`,
      humanoLinha: `Humanos: ${pctParticipacaoHumano}%`,
      destaque:
        pctParticipacaoBot >= 50
          ? 'O bot absorveu a maior parte do atendimento no período.'
          : 'Humanos concentraram a maior parte das conversas neste período.',
      barBotPct: pctParticipacaoBot,
      barHumanoPct: pctParticipacaoHumano,
      modoBarra: 'segmento',
    },
  ]
}

function buildGraficoDuasSeries(labels, cumComBot, cumSemBot) {
  return {
    economiaLabels: labels,
    graficoHorasComBot: cumComBot,
    graficoHorasSemBot: cumSemBot,
    graficoHorasPoupadasUltimo:
      cumComBot.length && cumSemBot.length
        ? Math.max(0, cumSemBot[cumSemBot.length - 1] - cumComBot[cumComBot.length - 1])
        : 0,
  }
}

/**
 * @param {string} tituloPeriodo — ex.: "Últimos 7 dias"
 */
export function createMockReport(tituloPeriodo) {
  const dias = ['24 mar', '25 mar', '26 mar', '27 mar', '28 mar', '29 mar', '30 mar']
  const cumComBot = [96, 192, 288, 384, 480, 576, 672]
  const cumSemBot = [320, 640, 960, 1280, 1520, 1680, 1818]
  const graf = buildGraficoDuasSeries(dias, cumComBot, cumSemBot)

  const botMin = 11
  const humMin = 60
  const velocidadeX = 5.4
  const pctTaxaBot = 76
  const pctTaxaHumano = 68
  const pctPartBot = 76
  const pctPartHum = 24

  const horasComTotal = cumComBot[cumComBot.length - 1]
  const horasSemTotal = cumSemBot[cumSemBot.length - 1]
  const horasPoupadasDec = Math.max(0, horasSemTotal - horasComTotal)
  const economiaSegundos = horasPoupadasDec * 3600
  const economiaReais = Math.round(horasPoupadasDec * VALOR_HORA_PADRAO_REAIS)
  const tempoTotalBotSeg = horasComTotal * 3600
  const tempoTotalAtendentesSeg = horasSemTotal * 3600
  const pctReducaoTempo = horasSemTotal > 0 ? Math.round((horasPoupadasDec / horasSemTotal) * 100) : 0

  const resumoExecutivo = `O bot foi ${String(velocidadeX).replace('.', ',')}× mais rápido que o atendimento humano e resolveu ${pctTaxaBot}% dos atendimentos no período.`
  const dataAteMock = '30 de mar.'
  const storytellingFrase = `O bot evitou ${formatarHorasMinutosDeSegundos(economiaSegundos)} de atendimento humano no período, reduzindo o esforço operacional da equipe.`

  return {
    periodSectionTitle: tituloPeriodo,
    economiaSegundosReal: economiaSegundos,
    economiaReais,
    headlineMoney: formatarMoedaBrl(economiaReais),
    headlineCustoTitulo: `${formatarMoedaBrl(economiaReais)} economizados`,
    horasEquivDecimais: horasPoupadasDec,
    horasEquivTexto: formatarHorasMinutosDeSegundos(economiaSegundos),
    horasEconomizadasLabel: `${formatarHorasMinutosDeSegundos(economiaSegundos)} economizadas`,
    storytellingFrase,
    graficoTitulo: 'Impacto do bot no tempo de atendimento',
    graficoSubtitulo: 'Comparação entre o tempo com bot e o tempo estimado sem automação no período.',
    graficoInsightReducao: pctReducaoTempo > 0
      ? `O bot reduziu o tempo de atendimento em aproximadamente ${pctReducaoTempo}% no período.`
      : 'Inclua atendimentos no período para ver a redução de tempo.',
    graficoAnotacaoLabel: formatarLabelHorasPoupadasData(economiaSegundos, dataAteMock),
    pctReducaoTempoOperacional: pctReducaoTempo,
    volumeRodapeTitulo: 'Tempo total no período',
    volumeBotLinha: `Com bot: ${formatarHorasMinutosDeSegundos(tempoTotalBotSeg)}`,
    volumeHumanoLinha: `Sem bot: ${formatarHorasMinutosDeSegundos(tempoTotalAtendentesSeg)}`,
    volumeExplicacaoSemBot: '“Sem bot” representa o tempo estimado de atendimento humano necessário para resolver o mesmo volume de conversas no período.',
    volumeMensagemFinal: 'O bot reduziu significativamente o tempo operacional da equipe no período.',
    tempoTotalBotMs: Math.round(tempoTotalBotSeg * 1000),
    tempoTotalBotMinutos: Math.round((tempoTotalBotSeg / 60) * 10) / 10,
    tempoTotalBotFormatado: formatarHorasMinutosDeSegundos(tempoTotalBotSeg),
    tempoTotalHumanoMs: Math.round(tempoTotalAtendentesSeg * 1000),
    tempoTotalHumanoMinutos: Math.round((tempoTotalAtendentesSeg / 60) * 10) / 10,
    tempoTotalHumanoFormatado: formatarHorasMinutosDeSegundos(tempoTotalAtendentesSeg),
    mediaRodapeTitulo: 'Tempo médio por atendimento',
    mediaBotLinha: `Bot: ${botMin} min`,
    mediaHumanoLinha: `Humano: ${humMin} min`,
    velocidadeX,
    botMinMedio: botMin,
    humanoMinComparacao: humMin,
    pctAutomatizado: 76,
    pctTaxaResolucaoBot: pctTaxaBot,
    pctTaxaResolucaoHumano: pctTaxaHumano,
    pctParticipacaoBot: pctPartBot,
    pctParticipacaoHumano: pctPartHum,
    conversasSemIntervencao: 363,
    resolvidasVariacaoPct: 27,
    totalConversas: 478,
    transferencias: 91,
    resumoExecutivo,
    insights: [
      'O bot resolveu 76% das conversas sem intervenção humana.',
      'Tempo médio do bot é 5,4x mais rápido que humanos.',
      `Foram evitadas ${formatarHorasMinutosDeSegundos(economiaSegundos)} de atendimento humano no período, frente ao cenário sem automação.`,
    ],
    csat: 4.3,
    pctSemTransferencia: 93,
    tempoMedioRespostaBot: '12s',
    metricaAuxTitulo: 'Atendimentos com bot',
    metricaAuxValor: '363',
    ...graf,
    comparativoBlocos: buildComparativoBlocos({
      botMinMedio: botMin,
      humanoMinComparacao: humMin,
      velocidadeX,
      pctTaxaBot,
      pctTaxaHumano,
      pctParticipacaoBot: pctPartBot,
      pctParticipacaoHumano: pctPartHum,
    }),
    legendaBotPct: pctPartBot,
  }
}

function ordenarDias(keys) {
  return [...keys].sort()
}

/**
 * Monta o objeto de exibição a partir do estado atual da página (após buscaResolvidos).
 */
export function mapApiStateToReport(state, tituloPeriodo) {
  const {
    numeroConversas,
    conversasResolvidasPeloBot,
    tempoResolucaoMediaMin,
    mediaCsat,
    totalRespostasCsat,
    resolucaoPorDia,
    transferenciasPorDia,
    resolvidos,
    periodoFim,
  } = state

  const lista = Array.isArray(resolvidos) ? resolvidos : []

  const {
    tempoTotalHumanoMs,
    tempoTotalHumanoMinutos,
    tempoTotalAtendentesSeg,
    tempoAtendentesPorDia,
  } = calcularTempoHumanoOcupadoMerge(lista)

  let somaSegHumComTempo = 0
  let qtdHumComTempo = 0
  lista.forEach(r => {
    const seg = calcularTempoAtendenteSegundos(r)
    if (seg > 0) {
      somaSegHumComTempo += seg
      qtdHumComTempo += 1
    }
  })

  const {
    tempoTotalBotMs,
    tempoTotalBotMinutos,
    tempoTotalBotSeg,
    tempoBotPorDia,
  } = calcularTempoBotOcupadoMerge(lista)

  let somaBotSegTotal = 0
  let qtdBotComTempo = 0
  lista.forEach(r => {
    const segBot = calcularTempoBotSegundos(r)
    if (segBot > 0) {
      somaBotSegTotal += segBot
      qtdBotComTempo += 1
    }
  })

  const economiaSegundosReal = Math.max(0, tempoTotalAtendentesSeg - tempoTotalBotSeg)
  const horasEquivDecimais = economiaSegundosReal / 3600
  const economiaReais = Math.round(horasEquivDecimais * VALOR_HORA_PADRAO_REAIS)
  const headlineMoney = formatarMoedaBrl(economiaReais)
  const headlineCustoTitulo = `${headlineMoney} economizados`
  const horasEquivTexto = formatarHorasMinutosDeSegundos(economiaSegundosReal)
  const horasEconomizadasLabel = `${horasEquivTexto} economizadas`
  const storytellingFrase = `O bot evitou ${horasEquivTexto} de atendimento humano no período, reduzindo o esforço operacional da equipe.`
  const dataAte = formatarDataAtePeriodo(periodoFim)
  const graficoAnotacaoLabel = formatarLabelHorasPoupadasData(economiaSegundosReal, dataAte)

  const pctReducaoTempoOperacional = tempoTotalAtendentesSeg > 0
    ? Math.round((economiaSegundosReal / tempoTotalAtendentesSeg) * 100)
    : 0
  const graficoInsightReducao = tempoTotalAtendentesSeg > 0 && economiaSegundosReal > 0
    ? `O bot reduziu o tempo de atendimento em aproximadamente ${pctReducaoTempoOperacional}% no período.`
    : 'Inclua atendimentos no período para ver a redução de tempo estimada.'

  const transferencias = Object.values(transferenciasPorDia || {}).reduce((a, b) => a + b, 0)

  const pctParticipacaoBot = numeroConversas > 0
    ? Math.round((conversasResolvidasPeloBot / numeroConversas) * 100)
    : 0
  const pctParticipacaoHumano = Math.max(0, 100 - pctParticipacaoBot)

  const statusResolvido = r => (r.status === 'resolvido' || r.status === 'RESOLVIDO')
  const resolvidoPeloBot = r => statusResolvido(r) && r.atendimento_bot === true
  const temFimBot = r => r.fim_bot != null && r.fim_bot !== ''
  const ehAtendimentoTransferidoRep = r => temFimBot(r) && r.atendente_ativo != null
  /** Resolução humana ou com repasse: exclui apenas “resolvido só pelo bot” (alinha aos cards do rodapé). */
  const resolvHumano = lista.filter(r => temFimBot(r) && statusResolvido(r)
    && !(resolvidoPeloBot(r) && !ehAtendimentoTransferidoRep(r))).length
  const pctTaxaResolucaoBot = numeroConversas > 0
    ? Math.round((conversasResolvidasPeloBot / numeroConversas) * 100)
    : 0
  const pctTaxaResolucaoHumano = numeroConversas > 0
    ? Math.round((resolvHumano / numeroConversas) * 100)
    : 0

  const botMinMedioCalculado = qtdBotComTempo > 0
    ? Math.max(1, Math.round(somaBotSegTotal / qtdBotComTempo / 60))
    : 0
  let botMinMedio = botMinMedioCalculado
  if (botMinMedio <= 0 && tempoResolucaoMediaMin > 0) {
    botMinMedio = Math.max(1, Math.round(tempoResolucaoMediaMin))
  }
  const humanoMinMedioReal = qtdHumComTempo > 0
    ? Math.max(1, Math.round(somaSegHumComTempo / qtdHumComTempo / 60))
    : 0
  const humanoMinComparacaoHeuristica = botMinMedio > 0 ? Math.min(120, Math.round(botMinMedio * 5)) : 50
  const humanoMinComparacao = humanoMinMedioReal > 0
    ? Math.min(120, humanoMinMedioReal)
    : humanoMinComparacaoHeuristica
  const velocidadeX = botMinMedio > 0 && humanoMinComparacao > 0
    ? Math.round((humanoMinComparacao / botMinMedio) * 10) / 10
    : 0

  const volumeBotLinha = `Com bot: ${formatarHorasMinutosDeSegundos(tempoTotalBotSeg)}`
  const volumeHumanoLinha = `Sem bot: ${formatarHorasMinutosDeSegundos(tempoTotalAtendentesSeg)}`
  const volumeExplicacaoSemBot = '“Sem bot” representa o tempo estimado de atendimento humano necessário para resolver o mesmo volume de conversas no período.'
  const volumeMensagemFinal = 'O bot reduziu significativamente o tempo operacional da equipe no período.'

  const keySet = new Set([
    ...Object.keys(resolucaoPorDia || {}),
    ...Object.keys(tempoAtendentesPorDia || {}),
    ...Object.keys(tempoBotPorDia || {}),
  ])
  const keys = ordenarDias([...keySet])
  let accCom = 0
  let accSem = 0
  const graficoHorasComBot = []
  const graficoHorasSemBot = []
  keys.forEach(k => {
    accCom += (tempoBotPorDia[k] || 0) / 3600
    accSem += (tempoAtendentesPorDia[k] || 0) / 3600
    graficoHorasComBot.push(Math.round(accCom * 100) / 100)
    graficoHorasSemBot.push(Math.round(accSem * 100) / 100)
  })

  const horasComExato = tempoTotalBotSeg / 3600
  const horasSemExato = tempoTotalAtendentesSeg / 3600
  if (graficoHorasComBot.length) {
    graficoHorasComBot[graficoHorasComBot.length - 1] = Math.round(horasComExato * 100) / 100
    graficoHorasSemBot[graficoHorasSemBot.length - 1] = Math.round(horasSemExato * 100) / 100
  }

  const economiaLabels = keys.length ? keys.map(formatarDataLabelPt) : ['—']
  let finalGrafCom = graficoHorasComBot
  let finalGrafSem = graficoHorasSemBot
  if (!keys.length) {
    finalGrafCom = [0]
    finalGrafSem = [0]
  }

  const ultimoPoupado = horasEquivDecimais

  const csatVal = totalRespostasCsat > 0 ? Math.round(mediaCsat * 10) / 10 : 0
  const pctSemTransf = numeroConversas > 0
    ? Math.max(0, Math.min(100, Math.round((1 - transferencias / numeroConversas) * 100)))
    : 0

  const refHumanoLabel = qtdHumComTempo > 0 ? 'o tempo humano médio medido' : 'a referência humana estimada'
  const resumoExecutivo = velocidadeX > 0 && pctTaxaResolucaoBot >= 0
    ? `O bot foi ${String(velocidadeX).replace('.', ',')}× mais rápido que ${refHumanoLabel} e resolveu ${pctTaxaResolucaoBot}% dos atendimentos com bot no período.`
    : 'Selecione um período com atendimentos pelo bot para ver o resumo comparativo.'

  const comparativoBlocos = buildComparativoBlocos({
    botMinMedio: botMinMedio || 0,
    humanoMinComparacao: humanoMinComparacao || 0,
    velocidadeX,
    pctTaxaBot: pctTaxaResolucaoBot,
    pctTaxaHumano: pctTaxaResolucaoHumano,
    pctParticipacaoBot,
    pctParticipacaoHumano,
  })

  return {
    periodSectionTitle: tituloPeriodo,
    economiaSegundosReal,
    economiaReais,
    headlineMoney,
    headlineCustoTitulo,
    horasEquivDecimais,
    horasEquivTexto,
    horasEconomizadasLabel,
    storytellingFrase,
    graficoTitulo: 'Impacto do bot no tempo de atendimento',
    graficoSubtitulo: 'Comparação entre o tempo com bot e o tempo estimado sem automação no período.',
    graficoInsightReducao,
    pctReducaoTempoOperacional,
    volumeRodapeTitulo: 'Tempo total no período',
    volumeBotLinha,
    volumeHumanoLinha,
    volumeExplicacaoSemBot,
    volumeMensagemFinal,
    tempoTotalBotMs,
    tempoTotalBotMinutos,
    tempoTotalBotFormatado: formatarHorasMinutosDeSegundos(tempoTotalBotSeg),
    tempoTotalHumanoMs,
    tempoTotalHumanoMinutos,
    tempoTotalHumanoFormatado: formatarHorasMinutosDeSegundos(tempoTotalAtendentesSeg),
    mediaRodapeTitulo: 'Tempo médio por atendimento',
    mediaBotLinha: botMinMedio > 0 ? `Bot: ${botMinMedio} min` : 'Bot: —',
    mediaHumanoLinha: humanoMinComparacao > 0 ? `Humano: ${humanoMinComparacao} min` : 'Humano: —',
    velocidadeX,
    botMinMedio: botMinMedio || '—',
    humanoMinComparacao: humanoMinComparacao || '—',
    pctAutomatizado: pctParticipacaoBot,
    pctTaxaResolucaoBot,
    pctTaxaResolucaoHumano,
    pctParticipacaoBot,
    pctParticipacaoHumano,
    conversasSemIntervencao: conversasResolvidasPeloBot,
    resolvidasVariacaoPct: 0,
    totalConversas: numeroConversas,
    transferencias,
    resumoExecutivo,
    insights: [
      numeroConversas > 0
        ? `O bot encerrou ${pctTaxaResolucaoBot}% das conversas com bot como resolvidas.`
        : 'Não há conversas com bot no período selecionado.',
      velocidadeX > 0
        ? `Tempo médio do bot é cerca de ${String(velocidadeX).replace('.', ',')}× menor que ${qtdHumComTempo > 0 ? 'o tempo humano médio medido' : 'a referência humana estimada'}.`
        : 'Inclua mais atendimentos resolvidos pelo bot para estimar a velocidade relativa.',
      economiaSegundosReal > 0
        ? `Foram evitadas ${horasEquivTexto} de atendimento humano no período, frente ao cenário sem automação.`
        : 'Ainda não há horas economizadas estimadas neste período.',
    ],
    csat: csatVal || '—',
    pctSemTransferencia: pctSemTransf,
    tempoMedioRespostaBot: '—',
    metricaAuxTitulo: 'Atendimentos com bot',
    metricaAuxValor: String(conversasResolvidasPeloBot || 0),
    economiaLabels,
    graficoHorasComBot: finalGrafCom.length ? finalGrafCom : [0],
    graficoHorasSemBot: finalGrafSem.length ? finalGrafSem : [0],
    graficoHorasPoupadasUltimo: ultimoPoupado,
    graficoAnotacaoLabel,
    comparativoBlocos,
    legendaBotPct: pctParticipacaoBot,
  }
}
